import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FcGoogle } from 'react-icons/fc';
import { authService } from '../services/authService';
import { useNotification } from '../contexts/NotificationContext';
import { getAuth, setPersistence, browserSessionPersistence, browserLocalPersistence } from 'firebase/auth';

export const LoginPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const auth = getAuth();

  const handleSocialLogin = async (provider: 'google') => {
    setIsLoading(true);
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      await authService.signInWithGoogle();
      addNotification('로그인 되었습니다.', 'success');
      navigate('/home');
    } catch (error: any) {
      console.error(`${provider} login error:`, error);
      if (error.code === 'auth/popup-closed-by-user') {
        setIsLoading(false);
        return;
      }
      let errorMessage = `${provider} 로그인 중 오류가 발생했습니다.`;
      if (error.code === 'auth/popup-blocked') {
        errorMessage = '팝업이 차단되었습니다. 브라우저에서 팝업을 허용해주세요.';
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = '로그인이 취소되었습니다.';
      }
      addNotification(errorMessage, 'error');
      setIsLoading(false);
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      await authService.signInWithEmail(email, password);
      addNotification('로그인 되었습니다.', 'success');
      navigate('/home');
    } catch (error: any) {
      console.error("Email login error:", error);
      let errorMessage = '이메일 로그인 중 오류가 발생했습니다.';
      if (error.code === 'auth/email-not-verified') {
        errorMessage = '가입 때 보낸 이메일 인증 링크를 먼저 확인해 주세요.';
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        errorMessage = '잘못된 이메일 또는 비밀번호입니다.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = '유효하지 않은 이메일 형식입니다.';
      }
      addNotification(errorMessage, 'error');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* 좌측: 배경 이미지 섹션 */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: 'url(/bulletine.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* 오버레이 그라데이션 */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/80 via-purple-900/70 to-pink-900/60" />

        {/* 좌측 하단 슬로건 */}
        <div className="absolute bottom-12 left-12 z-10 max-w-md">
          {/* 라인 1: 작은 폰트, 흰색 */}
          <p className="text-white/90 text-lg font-light mb-2">
            예배와 선교에 헌신된
          </p>

          {/* 라인 2-3: 큰 폰트, 2라인, Sky Blue → Purple 그라데이션 */}
          <h1 className="text-5xl font-bold leading-tight mb-4">
            <span className="bg-gradient-to-r from-sky-400 to-purple-500 bg-clip-text text-transparent">
              부다페스트
            </span>
            <br />
            <span className="bg-gradient-to-r from-sky-400 to-purple-500 bg-clip-text text-transparent">
              한인선교교회
            </span>
          </h1>

          {/* 라인 4: 작은 폰트, 흰색 */}
          <p className="text-white/80 text-base font-light">
            믿음과 순종으로 신앙의 성숙을 만들어갑니다
          </p>
        </div>

        {/* 장식 요소 */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/30 to-transparent" />
        <div className="absolute top-10 right-10 w-32 h-32 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl" />
      </div>

      {/* 우측: 로그인 폼 섹션 */}
      <div className="w-full lg:w-1/2 flex flex-col bg-gray-50 dark:bg-gray-900 p-8">
        {/* 폼 컨테이너 */}
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md">
            {/* 모바일에서만 보이는 슬로건 */}
            <div className="lg:hidden text-center mb-8">
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-1">예배와 선교에 헌신된</p>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-sky-400 to-purple-500 bg-clip-text text-transparent">
                부다페스트 한인선교교회
              </h2>
            </div>

            {/* 로고 (환영합니다 위) */}
            <div className="flex justify-center mb-6">
              <img src="/Main_logo.png" alt="Church Logo" className="h-14 w-auto" />
            </div>

            {/* 환영 메시지 */}
            <div className="mb-10 text-center">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                환영합니다 👋
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                계정에 로그인하여 시작하세요
              </p>
            </div>

            {/* 이메일 로그인 폼 */}
            <form onSubmit={handleEmailLogin} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  이메일
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  placeholder="name@example.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  비밀번호
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                  placeholder="••••••••"
                />
              </div>

              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded transition-colors"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                  로그인 상태 유지
                </label>
              </div>

              {/* 로그인 버튼 */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    로그인 중...
                  </span>
                ) : '로그인'}
              </button>
            </form>

            <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
              처음 오셨나요? <Link to="/signup" className="font-semibold text-indigo-600 underline underline-offset-4 dark:text-indigo-400">회원가입</Link>
            </p>

            {/* 구분선 */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                  또는
                </span>
              </div>
            </div>

            {/* Google 로그인 버튼 (하단) - 그라데이션 테두리 */}
            <div className="relative p-[2px] rounded-xl bg-gradient-to-r from-sky-400 to-purple-500">
              <button
                onClick={() => handleSocialLogin('google')}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-[10px] bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FcGoogle className="w-6 h-6" />
                <span>Google 계정으로 계속하기</span>
              </button>
            </div>

            {/* 푸터 */}
            <p className="mt-10 text-center text-sm text-gray-500 dark:text-gray-400">
              © 2025 부다페스트 한인선교교회. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
